"use client";

import Link from "next/link";
import { useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { INITIAL_FREE_PLAN_CREDITS, MONTHLY_STARTER_PLAN_CREDITS, MONTHLY_PRO_PLAN_CREDITS, TOPUP_CONFIG } from "@/lib/utils";
import styles from "./pricing.module.css";

const plans = [
  { name: "Starter", credits: MONTHLY_STARTER_PLAN_CREDITS, monthly: 14.99, yearly: 129.99 },
  { name: "Pro", credits: MONTHLY_PRO_PLAN_CREDITS, monthly: 29.99, yearly: 259.99 },
];

export default function HomePricing({ fullPage = false, className = "" }: { fullPage?: boolean; className?: string }) {
  const [tier, setTier] = useState(0);
  const [yearly, setYearly] = useState(false);
  const plan = plans[tier];
  return (
    <section className={`${styles.section} ${className}`} id="pricing" aria-labelledby="pricing-heading">
      <div className={styles.heading}>
        {fullPage ? <h1 id="pricing-heading">Pricing</h1> : <h2 id="pricing-heading">Pricing</h2>}
        <p>Start with {INITIAL_FREE_PLAN_CREDITS} free credits. Buy more credits or choose a monthly or yearly subscription.</p>
      </div>
      <div className={styles.cards}>
        <article className={styles.card}>
          <div className={styles.cardHeader}><h3>Pay as you go</h3></div>
          <p className={styles.planDescription}>Buy credits without a subscription.<br />All features and models are included with purchased credits.</p>
          <div className={styles.price}>${TOPUP_CONFIG.PRICE_PER_CREDIT.toFixed(2)}<span>/ credit</span></div>
          <p className={styles.billingDetail}>Buy credits from ${TOPUP_CONFIG.MIN_USD_AMOUNT}.</p>
          <Link className={styles.secondary} href="/signup?redirect=%2Fdashboard%2Fbilling%23buy-credits">Buy credits <ArrowRight size={16} /></Link>
          <ul><li><Check />Top up whenever you need more</li><li><Check />Form filling, data entry, and browser tasks</li><li><Check />No card needed for the free credits</li></ul>
        </article>
        <article className={`${styles.card} ${styles.subscription}`}>
          <div className={styles.cardHeader}>
            <h3>Subscription</h3>
            <div className={styles.billingSwitch} role="group" aria-label="Billing period">
              <button type="button" aria-pressed={!yearly} onClick={() => setYearly(false)}>Monthly</button>
              <button type="button" aria-pressed={yearly} onClick={() => setYearly(true)}>Yearly</button>
            </div>
          </div>
          <div className={styles.tierSwitch} role="group" aria-label="Monthly credit allowance">
            {plans.map((option, index) => (
              <button type="button" key={option.name} aria-label={`${option.credits.toLocaleString()} credits`} aria-pressed={tier === index} onClick={() => setTier(index)}>
                <span><strong>{option.credits.toLocaleString()}</strong><Check size={17} aria-hidden="true" /></span>
                <small>credits / month</small>
              </button>
            ))}
          </div>
          <div className={styles.price} aria-live="polite">${(yearly ? plan.yearly / 12 : plan.monthly).toFixed(2)}<span>/ month</span></div>
          <p className={styles.billingDetail} aria-live="polite">{yearly ? `$${plan.yearly.toFixed(2)} billed yearly. Save 28%.` : "Billed monthly. Cancel anytime."}</p>
          <Link className={styles.primary} href="/signup?redirect=%2Fdashboard%2Fbilling">Subscribe <ArrowRight size={16} /></Link>
          <ul><li><Check />All features and AI models</li><li><Check />Form filling, data entry, and browser tasks</li><li><Check />Buy extra credits when needed</li></ul>
        </article>
      </div>
      {!fullPage && <Link className={styles.details} href="/pricing">View pricing details <ArrowRight size={15} /></Link>}
    </section>
  );
}
